import React, { ReactNode, createContext, useEffect, useState } from "react"
import { useQuery } from "@apollo/client"
import { MY_SITE_QUERY } from "../entities/site/graphql/queries"
import { MY_SITE_SUBSCRIPTION } from "../entities/site/graphql/subscriptions"
import { ISite } from "../entities/site/interface"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { sendNativeLayerEvent } from "../utils/nativeLayerCommunication/sendNativeLayerEvent"
import { ENativeLayerOutgoingEventType } from "../utils/nativeLayerCommunication/interfaces"

dayjs.extend(utc)
dayjs.extend(timezone)
interface ISiteProviderProps {
    reinitializeApolloClient: () => Promise<void>
    children: ReactNode
}

export interface ISiteContext {
    site: ISite | undefined
    siteId: string
    setSiteId: (siteId: string) => Promise<void>
}

const SiteContext = createContext<ISiteContext | undefined>({} as ISiteContext)

export function SiteProvider({ reinitializeApolloClient, children }: ISiteProviderProps) {
    const [siteId, setSiteId] = useState<string>(
        JSON.parse(window.sessionStorage.getItem("selectedSite") ?? "{}").siteId ?? ""
    )

    const { data, subscribeToMore } = useQuery(MY_SITE_QUERY, { skip: !siteId })

    useEffect(() => {
        if (siteId) {
            subscribeToMore({
                document: MY_SITE_SUBSCRIPTION,
                variables: {
                    id: siteId,
                },
                updateQuery: (prev, { subscriptionData }) => {
                    if (!subscriptionData.data) return prev
                    return subscriptionData.data
                },
            })
        }
    }, [siteId, subscribeToMore])

    useEffect(() => {
        if (data?.mySite?.settings?.general?.timezone) {
            const timezone = data.mySite.settings.general.timezone
            dayjs.tz.setDefault(timezone)
            localStorage.setItem("siteTimezone", timezone)
        }
    }, [data])

    const _setSiteId = async (siteId: string) => {
        if (typeof window !== "undefined") {
            window.sessionStorage.setItem("selectedSite", JSON.stringify({ siteId }))
        }

        await reinitializeApolloClient()
        setSiteId(siteId)
        sendNativeLayerEvent({ type: ENativeLayerOutgoingEventType.SET_TENANT, tenantId: siteId })
    }

    const context: ISiteContext = {
        site: data?.mySite,
        siteId,
        setSiteId: _setSiteId,
    }

    return <SiteContext.Provider value={context}>{children}</SiteContext.Provider>
}

export default SiteContext
